import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { formatInTz } from '../lib/utcClock.js';
import { TIMEZONE_OPTIONS, DEFAULT_TIMEZONE } from '../lib/timezones.js';

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  async function submit(ev) {
    ev.preventDefault();
    setError('');
    setOk(false);
    if (form.newPassword !== form.confirmPassword) {
      setError('New password confirmation does not match');
      return;
    }
    if (form.newPassword.length < 10) {
      setError('New password must be at least 10 characters');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword
      });
      setOk(true);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (e) {
      setError(e?.data?.error ?? e.message ?? 'Change failed');
    } finally {
      setSaving(false);
    }
  }

  if (!user) return null;

  const tz = user.timezone || DEFAULT_TIMEZONE;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-ink-900">Settings</h2>
        <p className="text-sm text-ink-500">Your profile and credentials.</p>
      </div>

      {/* Two-column layout on ≥md. Left column groups identity + UI prefs
          (Profile, Timezone); right column is the credential-changing panel
          (Change password). No max-width cap — the grid fills the main area,
          so nothing but the page padding sits to the right of the columns.
          On narrow viewports everything stacks. */}
      <div className="grid gap-4 md:grid-cols-2 md:items-start">
        <div className="space-y-4">
          <section className="card p-6">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-ink-500">
              <i className="fa-solid fa-circle-user mr-2 text-ink-400" />
              Profile
            </h3>
            <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
              <dt className="text-ink-500">Username</dt>
              <dd className="font-mono text-ink-900">{user.username}</dd>
              <dt className="text-ink-500">Full name</dt>
              <dd className="text-ink-900">{user.fullname || <em className="text-ink-400">—</em>}</dd>
              <dt className="text-ink-500">Role</dt>
              <dd className="text-ink-900">{user.role}</dd>
              <dt className="text-ink-500">Created</dt>
              <dd className="font-mono text-xs text-ink-700">{formatInTz(user.created_at, tz)}</dd>
              <dt className="text-ink-500">Updated</dt>
              <dd className="font-mono text-xs text-ink-700">{formatInTz(user.updated_at, tz)}</dd>
            </dl>
          </section>

          <TimezoneSection user={user} onSaved={refreshUser} />
        </div>

        <div className="space-y-4">
          <section className="card p-6">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-ink-500">
              <i className="fa-solid fa-lock mr-2 text-ink-400" />
              Change password
            </h3>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Current password" required>
                <input
                  className="input"
                  type="password"
                  value={form.currentPassword}
                  onChange={(e) => update({ currentPassword: e.target.value })}
                  required
                  autoComplete="current-password"
                />
              </Field>
              <Field
                label="New password"
                required
                hint="At least 10 characters. Mix at least 3 of: lowercase, uppercase, digit, symbol. Avoid username and common words."
              >
                <input
                  className="input"
                  type="password"
                  value={form.newPassword}
                  onChange={(e) => update({ newPassword: e.target.value })}
                  required
                  minLength={10}
                  autoComplete="new-password"
                />
              </Field>
              <Field label="Confirm new password" required>
                <input
                  className="input"
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => update({ confirmPassword: e.target.value })}
                  required
                  minLength={10}
                  autoComplete="new-password"
                />
              </Field>

              {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
              {ok && <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Password changed.</div>}

              <div className="flex justify-end">
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? <><i className="fa-solid fa-spinner fa-spin-pulse" /> Saving…</> : 'Update password'}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>

      {/* Full-width KMS license panel sits below the two-column grid.
          Read-only view of state surfaced from /opt/adms/ad-kms-service
          via /api/license/*. Renew button is admin-only. */}
      <KmsLicenseSection user={user} tz={tz} />
    </div>
  );
}

// Per-user UI timezone. Saves via PATCH /auth/me, which is whitelisted to
// self-service preference fields on the server. After save we ask the auth
// context to reload /auth/me so every other page (sidebar clock, tables)
// picks up the new zone immediately — no reload needed.
function TimezoneSection({ user, onSaved }) {
  const [value, setValue] = useState(user.timezone || DEFAULT_TIMEZONE);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');
  const [error, setError] = useState('');

  const dirty = value !== (user.timezone || DEFAULT_TIMEZONE);

  async function save() {
    setSaving(true);
    setError('');
    setFlash('');
    try {
      await api.patch('/auth/me', { timezone: value });
      await onSaved?.();
      setFlash('Timezone saved.');
    } catch (e) {
      setError(e?.data?.error ?? e.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card p-6">
      <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-ink-500">
        <i className="fa-solid fa-globe mr-2 text-ink-400" />
        Timezone
      </h3>
      <p className="mb-4 text-xs text-ink-500">
        All requests, responses, and stored API data are processed in the UTC.
        This setting only affects the time display in the UI.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[320px] flex-1">
          <label className="mb-1 block text-sm font-semibold text-ink-800">Your timezone</label>
          <select
            className="input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={saving}
          >
            {TIMEZONE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={save}
          disabled={!dirty || saving}
          title={dirty ? 'Save timezone' : 'No changes'}
        >
          {saving
            ? <><i className="fa-solid fa-spinner fa-spin-pulse" /> Saving…</>
            : 'Save'}
        </button>
      </div>

      {flash && <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{flash}</div>}
      {error && <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
    </section>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-semibold text-ink-800">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <div className="mt-1 text-xs text-ink-500">{hint}</div>}
    </div>
  );
}

// ── KMS License widget ──────────────────────────────────────────────────
//
// Read-only surface for the local ad-kms-service IPC. /api/license/status
// returns the cached snapshot (cheap, kept fresh every 60s by the server-
// side poller). /api/license/info goes to live IPC for the extras —
// software_uuid, vCPU, microservice build, trusted-key role. Both fired
// in parallel on mount; if /info fails (IPC down) we still render with
// status alone.
//
// "Renew" calls POST /api/license/renew → ad-kms-service hits KMS online,
// validates the new Token, persists it. Synchronous, 0.5–2s typical, up
// to 30s. Admin-only.

const TONE = {
  emerald: 'bg-emerald-100 text-emerald-700',
  amber:   'bg-amber-100 text-amber-700',
  red:     'bg-red-100 text-red-700',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-700',
  slate:   'bg-slate-100 text-slate-700'
};

const STATUS_META = {
  active:                    { tone: 'emerald', icon: 'fa-circle-check',         label: 'Active' },
  active_with_warning:       { tone: 'amber',   icon: 'fa-triangle-exclamation', label: 'Active (warning)' },
  time_unsynced:             { tone: 'amber',   icon: 'fa-clock',                label: 'Time not synchronised' },
  not_activated:             { tone: 'red',     icon: 'fa-circle-xmark',         label: 'Not activated' },
  expired:                   { tone: 'red',     icon: 'fa-hourglass-end',        label: 'Expired' },
  revoked:                   { tone: 'red',     icon: 'fa-ban',                  label: 'Revoked' },
  hardware_mismatch:         { tone: 'red',     icon: 'fa-microchip',            label: 'Hardware mismatch' },
  software_uuid_mismatch:    { tone: 'red',     icon: 'fa-tag',                  label: 'Software UUID mismatch' },
  signature_invalid:         { tone: 'fuchsia', icon: 'fa-shield-halved',        label: 'Signature invalid' },
  tampering_detected:        { tone: 'fuchsia', icon: 'fa-skull-crossbones',     label: 'Tampering detected' },
  unsupported_token_version: { tone: 'red',     icon: 'fa-arrow-up-from-bracket',label: 'Unsupported token version' },
  unsupported_crypto_suite:  { tone: 'red',     icon: 'fa-arrow-up-from-bracket',label: 'Unsupported crypto suite' },
  ipc_unreachable:           { tone: 'slate',   icon: 'fa-plug-circle-xmark',    label: 'IPC unreachable' },
  unknown:                   { tone: 'slate',   icon: 'fa-circle-question',      label: 'Unknown' }
};

function StatusPill({ status }) {
  const m = STATUS_META[status] ?? STATUS_META.unknown;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${TONE[m.tone]}`}>
      <i className={`fa-solid ${m.icon}`} />
      {m.label}
    </span>
  );
}

function daysTone(days) {
  if (days == null) return TONE.slate;
  if (days <= 0)    return TONE.red;
  if (days < 30)    return TONE.amber;
  return TONE.emerald;
}

// IPC contract (host-product-integration.md §4.1 + ad-kms-service
// main.go): `expires_at` and `days_until_expiry` are emitted ONLY when
// the license has a finite expiration. For lifetime licenses the
// daemon omits both fields entirely (Go side: `if s.ExpiresAt != nil
// { resp["expires_at"] = ... }`). `maintenance_until` is always
// present when a license is loaded — it's a required Token field set
// at issue time. Rendering rule, per the spec:
//
//   if (expires_at)       → show date + remaining-days plate
//   else if (license OK)  → show "Lifetime" pill
//   else                  → em-dash (no license loaded yet)
//
// `lifetime` prop on DatePlate carries the second branch's verdict
// (caller decides — only the Expires-at plate uses it; maintenance is
// guaranteed non-null when license is loaded).
function DatePlate({ label, icon, iso, days, tz, lifetime = false }) {
  return (
    <div className="rounded-md border border-ink-200 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">
        {icon && <i className={`fa-solid ${icon} mr-1.5 text-ink-400`} />}
        {label}
      </div>
      <div className="mt-1 font-mono text-sm text-ink-900">
        {iso
          ? formatInTz(iso, tz)
          : lifetime
            ? <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                <i className="fa-solid fa-infinity" />
                Lifetime
              </span>
            : <em className="text-ink-400">—</em>}
      </div>
      {!lifetime && days != null && (
        <div className={`mt-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${daysTone(days)}`}>
          <i className="fa-solid fa-stopwatch" />
          {days > 0
            ? `${days} ${days === 1 ? 'day' : 'days'} remaining`
            : days === 0
              ? 'expires today'
              : `${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'} overdue`}
        </div>
      )}
    </div>
  );
}

// KMS Product Key fingerprint extractor.
//
// The daemon's request_renewal response includes `trusted_key_fp`
// (host-product-integration §4.3), but get_license_info does not — at
// least in the current build. Until the microservice exposes it on
// get_license_info too, we look in a few likely places: any
// future-proof top-level field, named keys inside the Token, and as a
// last resort a regex sweep of the Token JSON for the canonical
// `XX:XX:XX:…` colon-hex pattern.
//
// Returns a short form (first 6 octets, e.g. "73:00:c7:23:21:9d"), or
// null if no fingerprint-shaped string is anywhere in the payload.
function extractKmsFp(source) {
  if (!source) return null;
  for (const k of ['trusted_key_fp', 'kms_key_fp', 'kms_key_fingerprint', 'signing_key_fp']) {
    if (typeof source[k] === 'string' && source[k].length > 0) return shortFp(source[k]);
  }
  const tok = source.token;
  if (tok && typeof tok === 'object') {
    for (const k of ['kms_key_fp', 'kms_key_fingerprint', 'signing_key_fp', 'trusted_key_fp']) {
      if (typeof tok[k] === 'string' && tok[k].length > 0) return shortFp(tok[k]);
    }
    const m = JSON.stringify(tok).match(/\b([0-9a-fA-F]{2}(?::[0-9a-fA-F]{2}){7,})\b/);
    if (m) return shortFp(m[1]);
  }
  return null;
}

function shortFp(fp) {
  const parts = String(fp).split(':');
  return parts.slice(0, 6).join(':').toLowerCase();
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  function go() {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }).catch(() => {});
  }
  return (
    <button
      type="button"
      onClick={go}
      className="ml-1 text-ink-400 hover:text-ink-700"
      title={copied ? 'Copied' : 'Copy'}
    >
      <i className={`fa-solid ${copied ? 'fa-check text-emerald-600' : 'fa-copy'} text-xs`} />
    </button>
  );
}

function KmsLicenseSection({ user, tz }) {
  const isAdmin = user?.role === 'admin';

  const [snapshot, setSnapshot] = useState(null);
  const [extras, setExtras] = useState(null);          // result of /api/license/info, or null on failure
  const [renewKeyFp, setRenewKeyFp] = useState(null);  // captured from last successful renew (TZ §4.3)
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [renewing, setRenewing] = useState(false);
  const [flash, setFlash] = useState(null);            // { tone: 'emerald'|'red', message }

  async function load() {
    setLoading(true);
    setLoadError('');
    const [s, i] = await Promise.allSettled([
      api.get('/license/status'),
      api.get('/license/info')
    ]);
    if (s.status === 'fulfilled') {
      setSnapshot(s.value);
    } else {
      setLoadError(s.reason?.data?.error ?? s.reason?.message ?? 'Failed to read license status');
      setSnapshot(null);
    }
    setExtras(i.status === 'fulfilled' ? i.value : null);
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function renew() {
    if (!isAdmin) return;
    setRenewing(true);
    setFlash(null);
    try {
      const r = await api.post('/license/renew');
      // Debug breadcrumb so we can see in the toast exactly what the
      // server returned. Will be removed once the FP rendering is
      // confirmed working end-to-end.
      // eslint-disable-next-line no-console
      console.log('[license.renew] response', r);
      if (r?.ok) {
        // request_renewal returns trusted_key_fp; cache it so the
        // footer can show the current FP until the page reloads.
        if (r.trusted_key_fp) setRenewKeyFp(shortFp(r.trusted_key_fp));
        const fpInfo = r.trusted_key_fp ? ` · fp ${shortFp(r.trusted_key_fp)}` : ' · NO FP IN RESPONSE';
        setFlash({
          tone: 'emerald',
          message: (r.maintenance_until
            ? `License renewed. Maintenance until ${formatInTz(r.maintenance_until, tz)}.`
            : 'License renewed.') + fpInfo
        });
      } else {
        setFlash({ tone: 'red', message: `Renewal failed: ${r?.error ?? 'unknown error'}` });
      }
    } catch (e) {
      setFlash({
        tone: 'red',
        message: `Renewal request failed: ${e?.data?.error ?? e?.data?.detail ?? e.message ?? 'unknown error'}`
      });
    }
    await load();
    setRenewing(false);
  }

  return (
    <section className="card p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-500">
            <i className="fa-solid fa-shield-halved mr-2 text-ink-400" />
            KMS License
          </h3>
          <p className="mt-1 text-xs text-ink-500">
            For license renewals, migrations, maintenance extensions, and updates, please contact:{' '}
            <a href="mailto:support@micro-gis.com" className="font-mono text-ink-700 hover:text-ink-900 underline">
              support@micro-gis.com
            </a>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={load}
            disabled={loading || renewing}
            title="Reload license status"
          >
            <i className={`fa-solid fa-arrows-rotate ${loading ? 'fa-spin-pulse' : ''}`} />
            <span className="ml-2">Refresh</span>
          </button>
          {isAdmin && (
            <button
              type="button"
              className="btn-primary"
              onClick={renew}
              disabled={renewing || loading}
              title="Contact KMS now and refresh the license token"
            >
              {renewing
                ? <><i className="fa-solid fa-spinner fa-spin-pulse" /> Renewing…</>
                : <><i className="fa-solid fa-rotate" /> Renew license</>}
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{loadError}</div>
      )}

      {flash && (
        <div className={`mb-4 rounded-md px-3 py-2 text-xs ${flash.tone === 'emerald' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {flash.message}
        </div>
      )}

      {!snapshot && loading && (
        <div className="text-sm text-ink-500">
          <i className="fa-solid fa-spinner fa-spin-pulse mr-2" />Loading…
        </div>
      )}

      {snapshot && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <StatusPill status={snapshot.status} />
            {snapshot.last_checked_at && (
              <span className="text-xs text-ink-500">
                Last checked: <span className="font-mono">{formatInTz(snapshot.last_checked_at, tz)}</span>
              </span>
            )}
            {snapshot.source && (
              <span className="text-[10px] uppercase tracking-wider text-ink-400">via {snapshot.source}</span>
            )}
          </div>

          {snapshot.error && (
            <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
              IPC error: <code className="font-mono">{snapshot.error}</code>.
              Check <code className="font-mono">systemctl status ad-kms-service</code> on the VM.
            </div>
          )}

          {Array.isArray(snapshot.warnings) && snapshot.warnings.length > 0 && (
            <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <strong>Warnings from microservice:</strong>
              <ul className="mt-1 list-disc list-inside space-y-0.5">
                {snapshot.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <dl className="grid gap-y-2 text-sm md:grid-cols-[180px_1fr]">
            <dt className="text-ink-500">License ID</dt>
            <dd className="font-mono text-ink-900">
              {snapshot.license_id ?? <em className="text-ink-400">—</em>}
              {snapshot.license_id && <CopyBtn value={snapshot.license_id} />}
            </dd>

            <dt className="text-ink-500">Software UUID</dt>
            <dd className="font-mono text-xs text-ink-700">
              {extras?.software_uuid
                ? <>{extras.software_uuid}<CopyBtn value={extras.software_uuid} /></>
                : <em className="text-ink-400">—</em>}
            </dd>

            <dt className="text-ink-500">Hardware match</dt>
            <dd className="font-mono text-ink-900">
              {snapshot.hardware_match ?? <em className="text-ink-400">—</em>}
              {snapshot.hardware_match === '5/5' && <i className="fa-solid fa-check ml-1.5 text-emerald-600" />}
            </dd>

            <dt className="text-ink-500">vCPU</dt>
            <dd className="font-mono text-ink-700">{extras?.vcpu_count ?? <em className="text-ink-400">—</em>}</dd>
          </dl>

          {/* Per the IPC contract (host-product-integration.md §4.1):
              `expires_at` is omitted entirely for lifetime licenses.
              Treat its absence as the lifetime signal — `maintenance_
              until` is a separate date and isn't part of this check. */}
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <DatePlate
              label="Maintenance until"
              icon="fa-screwdriver-wrench"
              iso={snapshot.maintenance_until}
              days={snapshot.days_until_maintenance}
              tz={tz}
            />
            <DatePlate
              label="Expires at"
              icon="fa-hourglass-half"
              iso={snapshot.expires_at}
              days={snapshot.days_until_expiry}
              lifetime={!snapshot.expires_at}
              tz={tz}
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-ink-100 pt-3 text-[11px] text-ink-500">
            <span>
              <i className="fa-solid fa-microchip mr-1" />
              ad-kms-service: <span className="font-mono text-ink-700">{extras?.build ?? '?'}</span>
            </span>
            {extras?.trusted_key_role && (() => {
              const fp = renewKeyFp ?? extractKmsFp(extras);
              return (
                <span title={fp ? `KMS Product Key fingerprint: ${fp}` : undefined}>
                  <i className="fa-solid fa-key mr-1" />
                  trusted key: <span className="font-mono text-ink-700">{extras.trusted_key_role}</span>
                  {fp && <span className="ml-1 font-mono text-ink-700">({fp})</span>}
                </span>
              );
            })()}
          </div>
        </>
      )}
    </section>
  );
}
